import { deviceModel } from "../devices/catalog";
import { ICON_STROKE_UNITS, iconLabel } from "../icons/catalog";
import { chartHasTransparency } from "../scene/chart";
import { hasAlpha, isGradient, paintColors, parseHex } from "../scene/color";
import { isKnownFont } from "../scene/fonts";
import { boundingRect, flattenPage, rectsIntersect } from "../scene/geometry";
import { QR_PROBLEM_LABELS, qrMatrix, qrProblems, qrSummary } from "../scene/qr";
import { smallestFontSize, textColors, textFamilies } from "../scene/richtext";
import { ICON_VIEWBOX, type SceneDocument, type SceneElement } from "../scene/schema";
import { tableSummary } from "../scene/table";
import { textEffectTransparent } from "../scene/text-effects";
import { getPrintProvider, type PdfxStandard, PT_PER_PX } from "./providers";

/**
 * Document-level print checks. They run live in the export dialog and again
 * in the worker; the worker adds checks on the finished PDF (colour spaces,
 * fonts, ink coverage).
 */

export type PreflightSeverity = "error" | "warning" | "info";

export interface PreflightIssue {
  severity: PreflightSeverity;
  code: string;
  message: string;
  pageIndex?: number;
  elementId?: string;
}

export interface PreflightAsset {
  width: number | null;
  height: number | null;
  name?: string;
}

export interface PreflightSettings {
  provider?: string | undefined;
  bleedMm?: number | undefined;
  safeMm?: number | undefined;
  pdfx?: PdfxStandard | undefined;
  /** Font families of the organization (uploads, imported Google Fonts). */
  fonts?: readonly string[] | undefined;
}

const ELEMENT_LABELS: Record<SceneElement["type"], string> = {
  text: "Text",
  image: "Bild",
  shape: "Form",
  svg: "Grafik",
  qr: "QR-Code",
  icon: "Icon",
  device: "Mockup",
  path: "Zeichnung",
  chart: "Diagramm",
  group: "Gruppe",
  captions: "Untertitel",
  table: "Tabelle",
};

export function elementLabel(element: SceneElement, assetName?: string): string {
  if (element.name) return `„${element.name}“`;
  if (element.type === "text") {
    const snippet = element.text.trim().replace(/\s+/g, " ");
    return `Text „${snippet.length > 28 ? `${snippet.slice(0, 27)}…` : snippet || "…"}“`;
  }
  if (element.type === "qr") {
    const summary = qrSummary(element.value);
    return `QR-Code „${summary.length > 28 ? `${summary.slice(0, 27)}…` : summary || "…"}“`;
  }
  if (element.type === "icon") return `Icon „${iconLabel(element.icon)}“`;
  if (element.type === "device") return `Mockup „${deviceModel(element.device).name}“`;
  if (element.type === "chart" && element.title.trim()) {
    const title = element.title.trim();
    return `Diagramm „${title.length > 28 ? `${title.slice(0, 27)}…` : title}“`;
  }
  if (element.type === "table") {
    const summary = tableSummary(element);
    return summary ? `Tabelle „${summary}“` : "Tabelle";
  }
  if ((element.type === "image" || element.type === "svg") && assetName) {
    return `${ELEMENT_LABELS[element.type]} „${assetName}“`;
  }
  return ELEMENT_LABELS[element.type];
}

/** Effective resolution of an image element in dpi (lowest of both axes). */
export function effectiveDpi(
  element: Extract<SceneElement, { type: "image" }>,
  asset: PreflightAsset,
  pxPerInch: number,
): number | null {
  if (!asset.width || !asset.height) return null;
  if (element.crop) {
    const dpiX = (asset.width * element.crop.width) / (element.width / pxPerInch);
    const dpiY = (asset.height * element.crop.height) / (element.height / pxPerInch);
    return Math.round(Math.min(dpiX, dpiY));
  }
  // Displayed document pixels per source pixel: cover fills the box, contain fits into it.
  const scaleX = element.width / asset.width;
  const scaleY = element.height / asset.height;
  const scale = element.fit === "cover" ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
  return Math.round(pxPerInch / scale);
}

/** Smallest QR code side that phones read reliably from a flyer. */
const QR_MIN_SIDE_MM = 20;
/** Smallest module size (printing and camera focus). */
const QR_MIN_MODULE_MM = 0.4;

function isDarkNeutral(hex: string): boolean {
  const { r, g, b } = parseHex(hex);
  return r === g && g === b;
}

export function preflightDocument(
  doc: SceneDocument,
  assets: Record<string, PreflightAsset>,
  settings: PreflightSettings = {},
): PreflightIssue[] {
  const print = doc.format.print;
  if (!print) {
    return [
      {
        severity: "error",
        code: "not-print",
        message:
          "Diese Datei hat kein Druckformat. Lege für Flyer und Plakate ein Druck-Format an.",
      },
    ];
  }
  const provider = getPrintProvider(settings.provider ?? print.provider);
  const bleedMm = settings.bleedMm ?? print.bleedMm;
  const safeMm = settings.safeMm ?? print.safeMm;
  const pdfx = settings.pdfx ?? provider.pdfx;
  const pxPerMm = doc.format.width / print.widthMm;
  const pxPerInch = pxPerMm * 25.4;
  const ptPerDocPx = 72 / pxPerInch;
  const bleedPx = bleedMm * pxPerMm;
  const safePx = safeMm * pxPerMm;
  const width = doc.format.width;
  const height = doc.format.height;
  const issues: PreflightIssue[] = [];
  const add = (issue: PreflightIssue) => issues.push(issue);
  const tolerance = 0.5;

  if (bleedMm < provider.bleedMm && provider.id !== "custom") {
    add({
      severity: "warning",
      code: "bleed-small",
      message: `${provider.name} erwartet ${provider.bleedMm} mm Beschnitt, eingestellt sind ${bleedMm} mm.`,
    });
  }

  doc.pages.forEach((page, pageIndex) => {
    const flat = flattenPage(doc, pageIndex);
    const visible = flat.filter((item) => !item.hidden);
    const pageLabel = doc.pages.length > 1 ? `Seite ${pageIndex + 1}: ` : "";
    if (visible.length === 0 && !isGradient(page.background) && page.background === "#ffffff") {
      add({
        severity: "warning",
        code: "empty-page",
        message: `${pageLabel}Die Seite ist leer.`,
        pageIndex,
      });
    }
    const hiddenCount = flat.length - visible.length;
    if (hiddenCount > 0) {
      add({
        severity: "info",
        code: "hidden",
        message: `${pageLabel}${hiddenCount} ausgeblendete${hiddenCount === 1 ? "s Element wird" : " Elemente werden"} nicht gedruckt.`,
        pageIndex,
      });
    }
    if (
      pdfx !== "X-4" &&
      isGradient(page.background) &&
      page.background.stops.some((s) => hasAlpha(s.color))
    ) {
      add({
        severity: "error",
        code: "transparency",
        message: `${pageLabel}Der Seitenhintergrund nutzt Transparenz, das erlaubt PDF/${pdfx} nicht.`,
        pageIndex,
      });
    }

    for (const { element, box, opacity } of visible) {
      const assetName =
        element.type === "image" || element.type === "svg"
          ? assets[element.assetId]?.name
          : undefined;
      const label = `${pageLabel}${elementLabel(element, assetName)}`;
      const at = { pageIndex, elementId: element.id };
      const bounds = boundingRect(box);

      const pageArea = {
        x: -bleedPx,
        y: -bleedPx,
        width: width + 2 * bleedPx,
        height: height + 2 * bleedPx,
      };
      if (!rectsIntersect(bounds, pageArea)) {
        add({
          severity: "info",
          code: "outside",
          message: `${label} liegt außerhalb der Seite.`,
          ...at,
        });
        continue;
      }

      // Images: effective resolution
      if (element.type === "image") {
        const asset = assets[element.assetId];
        const dpi = asset ? effectiveDpi(element, asset, pxPerInch) : null;
        if (dpi !== null) {
          if (dpi < provider.minimumDpi) {
            add({
              severity: "error",
              code: "dpi-low",
              message: `${label} hat nur ${dpi} dpi (mindestens ${provider.minimumDpi}, empfohlen ${provider.recommendedDpi}). Es wird unscharf gedruckt.`,
              ...at,
            });
          } else if (dpi < provider.recommendedDpi) {
            add({
              severity: "warning",
              code: "dpi",
              message: `${label} hat ${dpi} dpi, empfohlen sind ${provider.recommendedDpi} dpi.`,
              ...at,
            });
          }
        }
      }

      // Backgrounds and images that end exactly at the trim edge
      if (element.type !== "text" && bleedPx > 0) {
        const edges: string[] = [];
        if (bounds.x <= tolerance && bounds.x > -bleedPx + tolerance) edges.push("links");
        if (bounds.y <= tolerance && bounds.y > -bleedPx + tolerance) edges.push("oben");
        const right = bounds.x + bounds.width;
        const bottom = bounds.y + bounds.height;
        if (right >= width - tolerance && right < width + bleedPx - tolerance) edges.push("rechts");
        if (bottom >= height - tolerance && bottom < height + bleedPx - tolerance)
          edges.push("unten");
        if (edges.length > 0) {
          add({
            severity: "warning",
            code: "bleed-missing",
            message: `${label} endet an der Schnittkante (${edges.join(", ")}). Ziehe es bis in den Beschnitt (${bleedMm} mm), sonst können weiße Blitzer entstehen.`,
            ...at,
          });
        }
      }

      if (element.type === "qr") {
        const behind = typeof page.background === "string" ? page.background : "#ffffff";
        for (const problem of qrProblems(element, behind)) {
          add({
            severity: problem === "too-long" ? "error" : "warning",
            code: `qr-${problem}`,
            message: `${label}: ${QR_PROBLEM_LABELS[problem]}`,
            ...at,
          });
        }
        const sideMm = Math.min(element.width, element.height) / pxPerMm;
        const matrix = qrMatrix(element.value, element.ecc);
        const moduleMm = matrix ? sideMm / (matrix.size + 2 * element.margin) : null;
        if (sideMm < QR_MIN_SIDE_MM) {
          add({
            severity: "warning",
            code: "qr-small",
            message: `${label} ist nur ${sideMm.toFixed(0)} mm groß. Für sicheres Scannen mindestens ${QR_MIN_SIDE_MM} mm × ${QR_MIN_SIDE_MM} mm.`,
            ...at,
          });
        } else if (moduleMm !== null && moduleMm < QR_MIN_MODULE_MM) {
          add({
            severity: "warning",
            code: "qr-dense",
            message: `${label}: Die Punkte sind nur ${moduleMm.toFixed(2)} mm klein. Kürzerer Inhalt (z. B. ein Kurzlink) oder ein größerer Code lassen sich besser scannen.`,
            ...at,
          });
        }
      }

      // Texts, graphics, QR codes and icons inside the safety margin
      if (
        (element.type === "text" ||
          element.type === "svg" ||
          element.type === "qr" ||
          element.type === "icon" ||
          element.type === "chart") &&
        safePx > 0
      ) {
        const insidePage =
          bounds.x < width &&
          bounds.y < height &&
          bounds.x + bounds.width > 0 &&
          bounds.y + bounds.height > 0;
        const tooClose =
          bounds.x < safePx - tolerance ||
          bounds.y < safePx - tolerance ||
          bounds.x + bounds.width > width - safePx + tolerance ||
          bounds.y + bounds.height > height - safePx + tolerance;
        if (insidePage && tooClose) {
          add({
            severity: "warning",
            code: "safe-zone",
            message: `${label} ragt in den Sicherheitsabstand (${safeMm} mm) und könnte angeschnitten werden.`,
            ...at,
          });
        }
      }

      if (element.type === "chart") {
        // Axis labels are three quarters of the base size.
        const sizePt = element.fontSize * 0.75 * ptPerDocPx;
        if (sizePt < 6) {
          add({
            severity: "warning",
            code: "text-small",
            message: `${label}: Die Beschriftung ist nur ${sizePt.toFixed(1)} pt groß. Unter 6 pt ist Text schwer lesbar – vergrößere die Schrift des Diagramms.`,
            ...at,
          });
        }
        if (!isKnownFont(element.fontFamily, settings.fonts)) {
          add({
            severity: "warning",
            code: "font-unknown",
            message: `${label}: Die Schrift „${element.fontFamily}“ ist nicht verfügbar und wird ersetzt.`,
            ...at,
          });
        }
      }

      if (element.type === "text") {
        for (const family of textFamilies(element)) {
          if (isKnownFont(family, settings.fonts)) continue;
          add({
            severity: "warning",
            code: "font-unknown",
            message: `${label}: Die Schrift „${family}“ ist nicht verfügbar und wird ersetzt.`,
            ...at,
          });
        }
        const sizePt = smallestFontSize(element) * ptPerDocPx;
        if (sizePt < 6) {
          add({
            severity: "warning",
            code: "text-small",
            message: `${label} ist nur ${sizePt.toFixed(1)} pt groß. Unter 6 pt ist Text schwer lesbar.`,
            ...at,
          });
        } else if (
          sizePt < 12 &&
          !isDarkNeutral(element.style.color) &&
          element.style.color.length <= 7
        ) {
          const { r, g, b } = parseHex(element.style.color);
          if (Math.max(r, g, b) < 90) {
            add({
              severity: "info",
              code: "text-rich",
              message: `${label}: Kleiner dunkler Text in einer Mischfarbe wird aus mehreren Druckfarben aufgebaut und kann unscharf wirken. Reines Schwarz (#000000) druckt schärfer.`,
              ...at,
            });
          }
        }
        if (
          element.style.stroke &&
          element.style.stroke.width * ptPerDocPx < provider.minLineWidthPt
        ) {
          add({
            severity: "warning",
            code: "line-thin",
            message: `${label}: Die Kontur ist dünner als ${provider.minLineWidthPt} pt.`,
            ...at,
          });
        }
      }

      if (
        (element.type === "shape" || element.type === "image") &&
        element.stroke &&
        element.stroke.width > 0
      ) {
        const widthPt = element.stroke.width * ptPerDocPx;
        if (widthPt < provider.minLineWidthPt) {
          add({
            severity: "warning",
            code: "line-thin",
            message: `${label}: Die Linie ist nur ${widthPt.toFixed(2)} pt stark (mindestens ${provider.minLineWidthPt} pt).`,
            ...at,
          });
        }
      }

      // Drawn strokes: their width scales with the box.
      if (element.type === "path") {
        const scale = Math.min(
          element.width / element.viewBox[0],
          element.height / element.viewBox[1],
        );
        const widthPt = element.strokeWidth * scale * ptPerDocPx;
        if (widthPt > 0 && widthPt < provider.minLineWidthPt) {
          add({
            severity: "warning",
            code: "line-thin",
            message: `${label}: Die Linie ist nur ${widthPt.toFixed(2)} pt stark (mindestens ${provider.minLineWidthPt} pt).`,
            ...at,
          });
        }
      }

      // Line icons: their lines shrink with the icon.
      if (element.type === "icon") {
        const units = ICON_STROKE_UNITS[element.weight];
        const widthPt =
          units === null
            ? null
            : ((units * Math.min(element.width, element.height)) / ICON_VIEWBOX) * ptPerDocPx;
        if (widthPt !== null && widthPt < provider.minLineWidthPt) {
          add({
            severity: "warning",
            code: "line-thin",
            message: `${label}: Die Linien sind nur ${widthPt.toFixed(2)} pt stark (mindestens ${provider.minLineWidthPt} pt). Vergrößere das Icon oder wähle einen kräftigeren Stil.`,
            ...at,
          });
        }
      }

      // Transparency
      const colors: string[] = [];
      if (element.type === "shape") colors.push(...paintColors(element.fill));
      if (element.type === "shape" && element.pattern) colors.push(element.pattern.color);
      if (element.type === "text") colors.push(...textColors(element));
      if (element.type === "qr") {
        colors.push(element.color);
        // A fully transparent background is simply not drawn.
        if (parseHex(element.background).a > 0) colors.push(element.background);
      }
      const alphaGradient =
        element.type === "shape" &&
        isGradient(element.fill) &&
        element.fill.stops.some((s) => hasAlpha(s.color));
      if (element.type === "icon" || element.type === "path") colors.push(element.color);
      if (element.type === "path" && element.fill) colors.push(element.fill);
      // The light layer of duotone icons is see-through.
      const layered =
        (element.type === "icon" && element.paths.some((path) => (path.opacity ?? 1) < 0.999)) ||
        (element.type === "chart" && chartHasTransparency(element));
      const transparent =
        opacity < 0.999 ||
        layered ||
        (element.type === "path" && element.brush === "highlighter") ||
        Boolean(element.shadow) ||
        (element.type === "text" && textEffectTransparent(element)) ||
        (element.type === "shape" && (element.pattern?.opacity ?? 1) < 0.999) ||
        colors.some((color) => hasAlpha(color));
      if (pdfx !== "X-4" && transparent) {
        add({
          severity: "error",
          code: "transparency",
          message: `${label} nutzt Transparenz oder Schatten. PDF/${pdfx} erlaubt das nicht – wähle PDF/X-4 oder entferne den Effekt.`,
          ...at,
        });
      } else if (alphaGradient) {
        add({
          severity: "warning",
          code: "gradient-alpha",
          message: `${label}: Verläufe mit Transparenz werden im Druck-PDF als Bild eingebettet und können weniger scharf sein.`,
          ...at,
        });
      }
    }
  });

  return issues;
}

/** px → pt for a print document (for the properties panel). */
export function docPxToPt(doc: SceneDocument, px: number): number {
  const print = doc.format.print;
  if (!print) return px * PT_PER_PX;
  return (px * print.widthMm * 72) / (doc.format.width * 25.4);
}

export function docPtToPx(doc: SceneDocument, pt: number): number {
  const print = doc.format.print;
  if (!print) return pt / PT_PER_PX;
  return (pt * doc.format.width * 25.4) / (print.widthMm * 72);
}

export function docPxToMm(doc: SceneDocument, px: number): number {
  const print = doc.format.print;
  return print ? (px * print.widthMm) / doc.format.width : (px * 25.4) / 96;
}

export function docMmToPx(doc: SceneDocument, mm: number): number {
  const print = doc.format.print;
  return print ? (mm * doc.format.width) / print.widthMm : (mm * 96) / 25.4;
}
